import React, { useMemo, useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Network,
  UserPlus,
  Users,
  Image as ImageIcon,
  Clock,
  Tag,
  Newspaper,
  Mail,
  Sparkles,
  IdCard,
  Pencil,
  ShieldCheck,
  Sparkle,
  Rocket,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * FeatureGuide
 * ─────────────────────────────────────────────────────────────────────────
 * A read-only, searchable Help Center surface that lives INSIDE GuideModal
 * as a tab. Lists every major KinsCircle feature with:
 *   • A short explanation
 *   • A direct action button (mapped to an existing route/scroll target)
 *   • An expandable "How it works" section
 *
 * It also renders:
 *   • A quick-start "Need help getting started?" card pinned to the top
 *   • A "How to Use KinsCircle" 5-step section at the bottom
 *
 * IMPORTANT — this component is purely UI / navigation. It does NOT touch
 * tree logic, parent_ids, spouse_id, the invite system, billing, auth,
 * Resend, the upload pipeline, or any database schema. Every "action"
 * button delegates to a callback prop that reuses an EXISTING route or
 * scroll target — there are no dead buttons.
 */

export type FeatureGuideAction =
  | 'tree'
  | 'gallery'
  | 'invite'
  | 'tour'
  | 'account'
  | 'feed';

export interface FeatureGuideProps {
  /** Run an existing in-app action — wired up by the parent (AppLayout via
   *  GuideModal). Each one is guaranteed to land on a real surface. */
  onAction: (action: FeatureGuideAction) => void;
}

interface FeatureCard {
  id: string;
  title: string;
  blurb: string;
  help: string;
  /** Keywords used by the live search filter. */
  keywords: string[];
  /** Action button label + which app surface it should land on. */
  cta: { label: string; action: FeatureGuideAction };
  Icon: React.ComponentType<{ className?: string }>;
  tone: string; // tailwind gradient classes for the icon tile
}

const FEATURES: FeatureCard[] = [
  {
    id: 'tree',
    title: 'Family Tree',
    blurb: 'Build and view your family connections.',
    help:
      'Start by adding your parents, then siblings, children, grandparents, spouses, and other relatives. The tree organizes everyone by generation automatically — you never need to drag, position, or rearrange anyone manually.',
    keywords: ['tree', 'family', 'graph', 'lineage', 'generation'],
    cta: { label: 'Open Tree', action: 'tree' },
    Icon: Network,
    tone: 'from-fuchsia-500 to-pink-500',
  },
  {
    id: 'add-member',
    title: 'Add Family Member',
    blurb: 'Add parents, children, siblings, spouses, half-siblings, and relatives.',
    help:
      'Parents are suggested first, but you can add family in any order. Tap any person on the tree and choose how the new person is related — KinsCircle places them in the correct generation. Missing relatives can always be added later.',
    keywords: ['add', 'parent', 'child', 'sibling', 'spouse', 'half', 'relative', 'in-law'],
    cta: { label: 'Add Family Member', action: 'tree' },
    Icon: UserPlus,
    tone: 'from-amber-500 to-orange-500',
  },
  {
    id: 'modern-family',
    title: 'Modern Family Support',
    blurb:
      'Supports co-parents, separated parents, spouses, half-siblings, and in-laws without forcing incorrect couple links.',
    help:
      'KinsCircle does not assume two parents are a couple. Half-siblings, step-parents, separated parents, and co-parents are all first-class. Each person controls their own side of the family — branches join only through shared children.',
    keywords: ['modern', 'co-parent', 'separated', 'half', 'step', 'in-law', 'divorce'],
    cta: { label: 'View Tree', action: 'tree' },
    Icon: Users,
    tone: 'from-purple-500 to-fuchsia-600',
  },
  {
    id: 'memories',
    title: 'Memories — Photos & Videos',
    blurb: 'Upload photos and videos to preserve family moments.',
    help:
      'Every upload can be assigned to a past date or marked as unknown date, and stays inside your family experience based on the privacy you choose. The same file is stored once even when it appears on multiple people\'s timelines.',
    keywords: ['photo', 'video', 'memory', 'media', 'upload', 'gallery'],
    cta: { label: 'Upload Memory', action: 'gallery' },
    Icon: ImageIcon,
    tone: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'timeline',
    title: 'Timeline / Journey',
    blurb: 'See memories in chronological order for each person.',
    help:
      'Tap a person\'s profile to view their life timeline. Memories are grouped by life stage and year, using the dates you assigned at upload. You can re-date any memory later without re-uploading the file.',
    keywords: ['timeline', 'journey', 'chronological', 'history', 'date'],
    cta: { label: 'Open Timeline', action: 'tree' },
    Icon: Clock,
    tone: 'from-sky-500 to-indigo-500',
  },
  {
    id: 'about-who',
    title: 'Who Is This Memory About?',
    blurb: 'Assign one memory to one or more family members.',
    help:
      'When you upload a photo or video, choose who it is about. The same file then appears on each selected person\'s timeline without duplicating the upload. Leave it blank to save it as a general Family Memory in the feed.',
    keywords: ['tag', 'assign', 'subject', 'about', 'who'],
    cta: { label: 'Upload Memory', action: 'gallery' },
    Icon: Tag,
    tone: 'from-rose-500 to-pink-500',
  },
  {
    id: 'feed',
    title: 'Family Feed',
    blurb: 'See recent photos, videos, memories, and updates across your family.',
    help:
      'The feed shows the latest activity across everyone in your family graph in reverse-chronological order. Tap any item to open the full memory or jump to the related profile.',
    keywords: ['feed', 'activity', 'recent', 'updates', 'news'],
    cta: { label: 'Open Family Feed', action: 'feed' },
    Icon: Newspaper,
    tone: 'from-indigo-500 to-purple-500',
  },
  {
    id: 'invite',
    title: 'Invite Family',
    blurb:
      'Invite relatives so they can claim their profile, add memories, and help complete the tree.',
    help:
      'Send a private link by email or share it directly. When a relative claims their card, they take over their own side of the family — without creating duplicate people or breaking the existing tree.',
    keywords: ['invite', 'share', 'email', 'link', 'claim'],
    cta: { label: 'Invite Family', action: 'invite' },
    Icon: Mail,
    tone: 'from-fuchsia-500 to-purple-500',
  },
  {
    id: 'suggestions',
    title: 'Smart Suggestions',
    blurb:
      'KinsCircle suggests your next best step, like adding parents, uploading a memory, tagging people, or inviting relatives.',
    help:
      'Suggestions are non-blocking — they appear next to your tree and after key actions. Follow them in order or skip them; nothing is forced and nothing is auto-created.',
    keywords: ['suggestions', 'tips', 'next step', 'guidance'],
    cta: { label: 'View Suggestions', action: 'tree' },
    Icon: Sparkles,
    tone: 'from-amber-500 to-pink-500',
  },
  {
    id: 'profile',
    title: 'Profile Cards',
    blurb:
      'Tap any family member to view their profile, memories, timeline, and relationship details.',
    help:
      'Profile cards open a side drawer with all of that person\'s memories, life events, relationships, and a chronological journey. From there you can add more memories or invite them to claim their profile.',
    keywords: ['profile', 'card', 'detail', 'drawer'],
    cta: { label: 'Open Tree', action: 'tree' },
    Icon: IdCard,
    tone: 'from-teal-500 to-emerald-500',
  },
  {
    id: 'edit-memory',
    title: 'Edit Memory Details',
    blurb:
      'Edit memory date, caption, and assigned family members later without re-uploading.',
    help:
      'Open any memory and tap edit. You can change the date (including marking it as unknown), update the caption, and re-assign which family members the memory is about. The file itself never changes.',
    keywords: ['edit', 'update', 'caption', 'date', 'reassign'],
    cta: { label: 'Open Memories', action: 'gallery' },
    Icon: Pencil,
    tone: 'from-orange-500 to-rose-500',
  },
  {
    id: 'privacy',
    title: 'Privacy / Family Access',
    blurb: 'Family memories stay within the family experience based on existing privacy rules.',
    help:
      'Every memory has a privacy setting: family-only or private. Family-only items are visible inside your family graph; private items are only visible to you. Privacy can be changed any time from Account settings.',
    keywords: ['privacy', 'access', 'security', 'visibility'],
    cta: { label: 'View Settings', action: 'account' },
    Icon: ShieldCheck,
    tone: 'from-slate-500 to-slate-700',
  },
];

const QUICK_START_STEPS = [
  { n: 1, text: 'Add yourself / your profile.' },
  { n: 2, text: 'Add your parents or your first family member.' },
  { n: 3, text: 'Upload a dated memory.' },
  { n: 4, text: 'Tag who the memory is about.' },
  { n: 5, text: 'Invite your family to help complete the tree.' },
] as const;

const FeatureGuide: React.FC<FeatureGuideProps> = ({ onAction }) => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FEATURES;
    return FEATURES.filter((f) => {
      const hay = [
        f.title,
        f.blurb,
        f.help,
        ...f.keywords,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  const toggle = (id: string) =>
    setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="space-y-5">
      {/* ── Need help getting started? ─────────────────────────────────── */}
      <div className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 p-4 sm:p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow-md">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-base sm:text-lg font-bold text-slate-900">
              Need help getting started?
            </h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Pick the quickest next step — KinsCircle handles the rest.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => onAction('tour')}
                className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-1.5 shadow"
              >
                <Sparkle className="w-4 h-4" />
                Continue Setup
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction('tree')}
                className="rounded-full border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 gap-1.5"
              >
                <UserPlus className="w-4 h-4" />
                Add Parent
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction('gallery')}
                className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5"
              >
                <ImageIcon className="w-4 h-4" />
                Upload First Memory
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction('invite')}
                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5"
              >
                <Mail className="w-4 h-4" />
                Invite Family
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Search ─────────────────────────────────────────────────────── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search features (e.g. timeline, invite, privacy)"
          aria-label="Search features"
          className="w-full pl-9 pr-3 py-2.5 rounded-full border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-300"
        />
      </div>

      {/* ── Feature cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3">
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
            <p className="text-sm text-slate-600">
              No features matched <span className="font-semibold">"{query}"</span>.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setQuery('')}
              className="mt-2 rounded-full text-fuchsia-700"
            >
              Clear search
            </Button>
          </div>
        )}

        {filtered.map((f) => {
          const Icon = f.Icon;
          const isOpen = !!expanded[f.id];
          return (
            <article
              key={f.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:border-fuchsia-200 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${f.tone} flex items-center justify-center shadow-md`}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-serif text-base font-bold text-slate-900 leading-tight">
                    {f.title}
                  </h4>
                  <p className="text-sm text-slate-600 mt-0.5">{f.blurb}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => onAction(f.cta.action)}
                      className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-1.5 shadow"
                    >
                      {f.cta.label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggle(f.id)}
                      className="rounded-full text-slate-600 hover:bg-slate-100 gap-1"
                      aria-expanded={isOpen}
                      aria-controls={`feature-help-${f.id}`}
                    >
                      How it works
                      {isOpen ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>

                  {isOpen && (
                    <div
                      id={`feature-help-${f.id}`}
                      className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3"
                    >
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {f.help}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* ── How to Use KinsCircle (quick start) ────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow">
            <Sparkle className="w-4 h-4 text-white" />
          </div>
          <h3 className="font-serif text-base sm:text-lg font-bold text-slate-900">
            How to Use KinsCircle
          </h3>
        </div>
        <ol className="space-y-2">
          {QUICK_START_STEPS.map((s) => (
            <li key={s.n} className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white text-xs font-bold flex items-center justify-center shadow">
                {s.n}
              </span>
              <span className="text-sm text-slate-700 leading-relaxed pt-0.5">
                {s.text}
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => onAction('tree')}
            className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-1.5 shadow"
          >
            <Network className="w-4 h-4" />
            Start with the Tree
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAction('tour')}
            className="rounded-full border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 gap-1.5"
          >
            <Sparkles className="w-4 h-4" />
            Replay First-time Tour
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeatureGuide;
